import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
	ListBeacon4PerfConfigs,
	CreateBeacon4PerfConfig,
	UpdateBeacon4PerfConfig,
	DeleteBeacon4PerfConfig,
	ValidateBeacon4PerfSql,
	ListBeacon4PerfCatalog,
	ListBeacon4PerfCatalogColumns,
	ListBeacon4PerfSqlFunctions,
	ListBeacon4PerfMappings,
	CreateBeacon4PerfMapping,
	UpdateBeacon4PerfMapping,
	DeleteBeacon4PerfMapping,
	ListBeacon4PerfAudit,
	RollbackBeacon4PerfAudit,
} from '../requests/Beacon4PerformanceNetworkOverviewAdminRequests';

// ── Thunks ──────────────────────────────────────────────────────────────────
export const fetchAdminConfigs = createAsyncThunk(
	'beacon4PerformanceNetworkOverviewAdmin/fetchConfigs',
	async (filters, { rejectWithValue }) => {
		try {
			const res = await ListBeacon4PerfConfigs(filters);
			return res?.data ?? res;
		} catch (e) { return rejectWithValue(e.response?.data ?? e.message); }
	}
);

export const createAdminConfig = createAsyncThunk(
	'beacon4PerformanceNetworkOverviewAdmin/createConfig',
	async (dto, { dispatch, rejectWithValue }) => {
		try {
			// POST kpis answers { id }. It used to be discarded and the thunk
			// returned true; the id is now needed so the caller can select the new
			// KPI and drop the user on the Formula tab, which is where the data
			// source and formula are set since the create dialog stopped asking.
			const res = await CreateBeacon4PerfConfig(dto);
			await dispatch(fetchAdminConfigs({}));
			return { id: (res?.data ?? res)?.id ?? null };
		} catch (e) { return rejectWithValue(e.response?.data ?? e.message); }
	}
);

export const updateAdminConfig = createAsyncThunk(
	'beacon4PerformanceNetworkOverviewAdmin/updateConfig',
	async ({ id, dto }, { dispatch, rejectWithValue }) => {
		try {
			await UpdateBeacon4PerfConfig(id, dto);
			await dispatch(fetchAdminConfigs({}));
			return true;
		} catch (e) { return rejectWithValue(e.response?.data ?? e.message); }
	}
);

export const deleteAdminConfig = createAsyncThunk(
	'beacon4PerformanceNetworkOverviewAdmin/deleteConfig',
	async ({ id, note }, { dispatch, rejectWithValue }) => {
		try {
			await DeleteBeacon4PerfConfig(id, note);
			await dispatch(fetchAdminConfigs({}));
			return true;
		} catch (e) { return rejectWithValue(e.response?.data ?? e.message); }
	}
);

export const validateAdminSql = createAsyncThunk(
	'beacon4PerformanceNetworkOverviewAdmin/validateSql',
	async (dto, { rejectWithValue }) => {
		try {
			const res = await ValidateBeacon4PerfSql(dto);
			return res?.data ?? res;
		} catch (e) { return rejectWithValue(e.response?.data ?? e.message); }
	}
);

// ── Source catalog ──────────────────────────────────────────────────────────
export const fetchAdminCatalog = createAsyncThunk(
	'beacon4PerformanceNetworkOverviewAdmin/fetchCatalog',
	async (tech, { rejectWithValue }) => {
		try {
			const res = await ListBeacon4PerfCatalog(tech);
			return res?.data ?? res;
		} catch (e) { return rejectWithValue(e.response?.data ?? e.message); }
	}
);

export const fetchAdminCatalogColumns = createAsyncThunk(
	'beacon4PerformanceNetworkOverviewAdmin/fetchCatalogColumns',
	async (catalogId, { rejectWithValue }) => {
		try {
			const res = await ListBeacon4PerfCatalogColumns(catalogId);
			return res?.data ?? res;
		} catch (e) { return rejectWithValue(e.response?.data ?? e.message); }
	}
);

export const fetchAdminSqlFunctions = createAsyncThunk(
	'beacon4PerformanceNetworkOverviewAdmin/fetchSqlFunctions',
	async (_, { rejectWithValue }) => {
		try {
			const res = await ListBeacon4PerfSqlFunctions();
			return res?.data ?? res;
		} catch (e) { return rejectWithValue(e.response?.data ?? e.message); }
	}
);

export const fetchAdminMappings = createAsyncThunk(
	'beacon4PerformanceNetworkOverviewAdmin/fetchMappings',
	async (mainKpiId, { rejectWithValue }) => {
		try {
			const res = await ListBeacon4PerfMappings(mainKpiId);
			return res?.data ?? res;
		} catch (e) { return rejectWithValue(e.response?.data ?? e.message); }
	}
);

export const createAdminMapping = createAsyncThunk(
	'beacon4PerformanceNetworkOverviewAdmin/createMapping',
	async ({ dto, refreshFor }, { dispatch, rejectWithValue }) => {
		try {
			await CreateBeacon4PerfMapping(dto);
			if (refreshFor != null) await dispatch(fetchAdminMappings(refreshFor));
			return true;
		} catch (e) { return rejectWithValue(e.response?.data ?? e.message); }
	}
);

export const updateAdminMapping = createAsyncThunk(
	'beacon4PerformanceNetworkOverviewAdmin/updateMapping',
	async ({ id, dto, refreshFor }, { dispatch, rejectWithValue }) => {
		try {
			await UpdateBeacon4PerfMapping(id, dto);
			if (refreshFor != null) await dispatch(fetchAdminMappings(refreshFor));
			return true;
		} catch (e) { return rejectWithValue(e.response?.data ?? e.message); }
	}
);

export const deleteAdminMapping = createAsyncThunk(
	'beacon4PerformanceNetworkOverviewAdmin/deleteMapping',
	async ({ id, note, refreshFor }, { dispatch, rejectWithValue }) => {
		try {
			await DeleteBeacon4PerfMapping(id, note);
			if (refreshFor != null) await dispatch(fetchAdminMappings(refreshFor));
			return true;
		} catch (e) { return rejectWithValue(e.response?.data ?? e.message); }
	}
);

export const fetchAdminAudit = createAsyncThunk(
	'beacon4PerformanceNetworkOverviewAdmin/fetchAudit',
	async ({ table, id }, { rejectWithValue }) => {
		try {
			const res = await ListBeacon4PerfAudit(table, id);
			return res?.data ?? res;
		} catch (e) { return rejectWithValue(e.response?.data ?? e.message); }
	}
);

export const rollbackAdminAudit = createAsyncThunk(
	'beacon4PerformanceNetworkOverviewAdmin/rollbackAudit',
	async ({ auditId, note }, { rejectWithValue }) => {
		try {
			await RollbackBeacon4PerfAudit(auditId, note);
			return true;
		} catch (e) { return rejectWithValue(e.response?.data ?? e.message); }
	}
);

// ── Slice ───────────────────────────────────────────────────────────────────
const slice = createSlice({
	name: 'beacon4PerformanceNetworkOverviewAdmin',
	initialState: {
		configs:        [],
		mappings:       [],
		audit:          [],
		validation:     null,
		catalog:        [],
		// Keyed by catalog id, not a single list.
		//
		// The Formula tab and the New/Edit dialog are mounted at the same time
		// and both read this. With one shared array, opening the dialog wiped the
		// tab's column rail, picking a view in the dialog replaced it with a
		// different view's columns, and two in-flight requests could resolve out
		// of order and leave the wrong columns on screen. Keying by id makes each
		// consumer read only what it asked for.
		catalogColumns: {},
		sqlFunctions:   [],
		catalogLoading: false,
		loading:        false,
		saving:         false,
		error:          null,
	},
	reducers: {
		clearValidation(state) { state.validation = null; },
		clearAudit(state)      { state.audit = []; },
	},
	extraReducers: (b) => {
		b.addCase(fetchAdminConfigs.pending,  (s) => { s.loading = true; s.error = null; })
		 .addCase(fetchAdminConfigs.fulfilled, (s, a) => { s.loading = false; s.configs = a.payload ?? []; })
		 .addCase(fetchAdminConfigs.rejected,  (s, a) => { s.loading = false; s.error = a.payload; })

		 .addCase(createAdminConfig.pending,  (s) => { s.saving = true; })
		 .addCase(createAdminConfig.fulfilled,(s) => { s.saving = false; })
		 .addCase(createAdminConfig.rejected, (s, a) => { s.saving = false; s.error = a.payload; })

		 .addCase(updateAdminConfig.pending,  (s) => { s.saving = true; })
		 .addCase(updateAdminConfig.fulfilled,(s) => { s.saving = false; })
		 .addCase(updateAdminConfig.rejected, (s, a) => { s.saving = false; s.error = a.payload; })

		 .addCase(deleteAdminConfig.pending,  (s) => { s.saving = true; })
		 .addCase(deleteAdminConfig.fulfilled,(s) => { s.saving = false; })
		 .addCase(deleteAdminConfig.rejected, (s, a) => { s.saving = false; s.error = a.payload; })

		 .addCase(validateAdminSql.fulfilled, (s, a) => { s.validation = a.payload; })
		 .addCase(validateAdminSql.rejected,  (s, a) => { s.validation = { valid: false, errors: ['Validation failed'] }; })

		 .addCase(fetchAdminCatalog.fulfilled, (s, a) => { s.catalog = a.payload ?? []; })
		 .addCase(fetchAdminCatalog.rejected,  (s, a) => { s.catalog = []; s.error = a.payload; })

		 // a.meta.arg is the catalogId the thunk was dispatched with, which is how
		 // each response is filed under the view it actually belongs to.
		 .addCase(fetchAdminCatalogColumns.pending,  (s) => { s.catalogLoading = true; })
		 // Array.isArray, not just ?? [] — both consumers immediately .map/.filter
		 // this, so a non-array response would take the whole panel down.
		 .addCase(fetchAdminCatalogColumns.fulfilled,(s, a) => { s.catalogLoading = false; s.catalogColumns[a.meta.arg] = Array.isArray(a.payload) ? a.payload : []; })
		 // A 404 here means the view is gone or was deactivated. Record the empty
		 // result so the UI can say so instead of showing a stale list.
		 .addCase(fetchAdminCatalogColumns.rejected, (s, a) => { s.catalogLoading = false; s.catalogColumns[a.meta.arg] = []; s.error = a.payload; })

		 .addCase(fetchAdminSqlFunctions.fulfilled, (s, a) => { s.sqlFunctions = a.payload ?? []; })

		 .addCase(fetchAdminMappings.pending, (s) => { s.loading = true; })
		 .addCase(fetchAdminMappings.fulfilled,(s, a) => { s.loading = false; s.mappings = a.payload ?? []; })
		 .addCase(fetchAdminMappings.rejected, (s, a) => { s.loading = false; s.error = a.payload; })

		 .addCase(fetchAdminAudit.fulfilled, (s, a) => { s.audit = a.payload ?? []; });
	},
});

export const { clearValidation, clearAudit } = slice.actions;
export default slice.reducer;

export const selectAdminConfigs    = (state) => state.beacon4PerformanceNetworkOverviewAdmin.configs;
export const selectAdminMappings   = (state) => state.beacon4PerformanceNetworkOverviewAdmin.mappings;
export const selectAdminAudit      = (state) => state.beacon4PerformanceNetworkOverviewAdmin.audit;
export const selectAdminValidation = (state) => state.beacon4PerformanceNetworkOverviewAdmin.validation;
export const selectAdminLoading    = (state) => state.beacon4PerformanceNetworkOverviewAdmin.loading;
export const selectAdminSaving     = (state) => state.beacon4PerformanceNetworkOverviewAdmin.saving;

export const selectAdminCatalog        = (state) => state.beacon4PerformanceNetworkOverviewAdmin.catalog;
// Map of catalogId -> columns. Consumers index it by the id they are showing;
// see the note on the initial state for why this isn't a single list.
export const selectAdminCatalogColumnsMap = (state) => state.beacon4PerformanceNetworkOverviewAdmin.catalogColumns;
export const selectAdminCatalogLoading = (state) => state.beacon4PerformanceNetworkOverviewAdmin.catalogLoading;
export const selectAdminSqlFunctions   = (state) => state.beacon4PerformanceNetworkOverviewAdmin.sqlFunctions;
